'use client';

import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { safeLocalStorage } from '@/lib/safeLocalStorage';

export type ThemeModeType = 'light' | 'dark' | 'system';

export interface ThemeContextProps {
  themeMode: ThemeModeType;
  setThemeMode: (modeName: ThemeModeType) => void;
  showToggle: boolean;
}

export const ThemeContext = createContext<ThemeContextProps | undefined>(
  undefined,
);

// ThemeProvider is an ongoing WIP feature since it's much more difficult to add this in down the road
// You can enable this by setting a ?theme-toggle=visible URL param
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeMode, updateThemeMode] = useState<ThemeModeType>('light');
  const [showToggle, setShowToggle] = useState(false);

  // Handle the visibility of the toggle (separate for easy removal later)
  useEffect(() => {
    const { searchParams } = new URL(window?.location.href);
    const toggleParam = searchParams?.get('theme-toggle');
    const toggleStorage = safeLocalStorage.getItem('theme-toggle');

    if (toggleParam === 'visible' || toggleStorage === 'visible') {
      setShowToggle(true);
    }

    if (showToggle) {
      safeLocalStorage.setItem('theme-toggle', 'visible');
      console.log('Set theme toggle to visible');
    }
  }, [showToggle]);

  // Handle Theme setting on load, then set React/provider state
  // htmlAttr is source of truth, as that gets loaded before React (and reads from LS)
  useEffect(() => {
    const htmlAttr = document.documentElement.getAttribute(
      'data-theme',
    ) as ThemeModeType;

    if (htmlAttr) {
      updateThemeMode(htmlAttr);
    }
  }, []);

  function handleThemeUpdate(modeName: ThemeModeType) {
    // state (for provider and application-wide state)
    updateThemeMode(modeName);

    // LocalStorage (so onLoad can "remember")
    safeLocalStorage.setItem('theme-mode', modeName);

    // HTML attr (for actual styles)
    document.documentElement.setAttribute('data-theme', modeName);
  }

  return (
    <ThemeContext.Provider
      value={{
        themeMode,
        setThemeMode: handleThemeUpdate,
        showToggle,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}
